import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import moment from "moment";
import { from, of } from "rxjs";
import { catchError, finalize, map, switchMap, take } from "rxjs/operators";
import { LoaderService } from "../../shared/loading/loader.service";
import { AuthService } from "../auth.service";

@Injectable()
export class AuthGuard implements CanActivateChild {
  constructor(
    private auth: AuthService,
    private router: Router,
    private loading: LoaderService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return from(this.auth.waitForAuthReady()).pipe(
      switchMap(() => this.auth.getToken().pipe(take(1))),
      map((token) => !!token),
      map((loggedIn) => {
        if (!loggedIn) {
          if (state.url.includes("sync=google")) {
            window.location.href = "https://gsignature.com/sign-up";
          }
          console.log("access denied");
          localStorage.setItem("returnUrl", state.url);
          void this.router.navigate(["/login"]);
          return false;
        }
        return true;
      }),
      switchMap((loggedIn) => {
        if (!loggedIn) {
          return of(false);
        }

        const ensureUserLoaded = this.auth.appUser
          ? Promise.resolve(true)
          : this.auth.fetchCurrent();

        if (!this.auth.appUser) {
          this.loading.setLoading(true);
        }

        return from(ensureUserLoaded).pipe(
          map((loaded): boolean | UrlTree => {
            if (loaded === false || !this.auth.appUser) {
              return false;
            }

            const isOnboarding = state.url.includes("/onboarding");
            const path = state.url.split("?")[0];

            if (
              !this.auth.isSuperAdmin &&
              !this.auth.isGroupManager &&
              !this.auth.isReseller &&
              this.auth.trialFinishDate &&
              moment().isAfter(
                moment(this.auth.trialFinishDate as string | Date),
              )
            ) {
              void this.router.navigate(["/expired-trial"]);
              return true;
            }

            if (
              !isOnboarding &&
              !this.auth.appUser.planId &&
              !this.auth.appUser.isPremium
            ) {
              void this.router.navigate(["/plan"]);
              return false;
            }

            if (
              next.data.adminRoute &&
              !(
                this.auth.appUser.isAdmin ||
                this.auth.appUser.role === "SUPER_ADMIN"
              )
            ) {
              void this.auth.logOut();
              return false;
            }

            if (next.data.managerRoute && !this.auth.hasCompanyManagerAccess) {
              void this.router.navigate(["/no-access"]);
              return false;
            }

            if (next.data.superAdminRoute && !this.auth.isSuperAdmin) {
              void this.router.navigate(["/no-access"]);
              return false;
            }

            if (path === "/accounts" && !this.auth.canChangeAccounts) {
              return this.router.createUrlTree(
                this.auth.getDefaultAppCommands(),
              );
            }

            if (!this.auth.isSuperAdmin) {
              const oi = this.auth.appUser?.optionalInterfaces;
              const isCardOrdersRoute = /^\/?card-orders/.test(state.url);
              if (
                isCardOrdersRoute &&
                (!oi?.digitalCard || !oi?.selfPrintCards)
              ) {
                void this.router.navigate(["/no-access"]);
                return false;
              }

              const isDigitalCardsRoute =
                /^\/?(digital-cards|my-digital-cards)/.test(state.url);
              if (isDigitalCardsRoute && !oi?.digitalCard) {
                void this.router.navigate(["/no-access"]);
                return false;
              }

              if (
                /^\/?print-cards/.test(state.url) &&
                (!oi?.digitalCard || !oi?.selfPrintCards)
              ) {
                void this.router.navigate(["/no-access"]);
                return false;
              }

              if (
                /^\/?digital-cards\/sources/.test(state.url) &&
                !oi?.contextualQr
              ) {
                void this.router.navigate(["/no-access"]);
                return false;
              }

              if (
                /^\/?(backgrounds|my-backgrounds)/.test(state.url) &&
                !oi?.virtualBackgrounds
              ) {
                void this.router.navigate(["/no-access"]);
                return false;
              }

              if (
                /^\/?settings\/extension/.test(state.url) &&
                !oi?.browserExtension
              ) {
                void this.router.navigate(["/no-access"]);
                return false;
              }

              if (
                /^\/?(signatures|planned|stats)/.test(state.url) &&
                !oi?.emailSignatures
              ) {
                void this.router.navigate(["/no-access"]);
                return false;
              }
            }

            if (
              !isOnboarding &&
              !this.auth.isSuperAdmin &&
              this.auth.hasCompanyManagerAccess &&
              !["COMPLETED", "SKIPPED"].includes(
                (this.auth.appUser.onboarding || "").toString(),
              )
            ) {
              const queryParams: Record<string, string> = { fromStart: "true" };
              if (this.auth.currentDomain) {
                queryParams["domain"] = this.auth.currentDomain;
              }
              void this.router.navigate(["/onboarding"], { queryParams });
              return false;
            }

            return true;
          }),
          catchError(() => {
            void this.auth.logOut();
            return of(false);
          }),
        );
      }),
      finalize(() => {
        this.loading.setLoading(false);
      }),
    );
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(next, state);
  }
}
